import React from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Briefcase, Plus, Send, Maximize2, History, Paperclip, MessageSquare,
  StickyNote, Calendar, ArrowUpRight, Users, Link2, Phone, Mail, Tag,
  ExternalLink, FileIcon, Image, FileText,
} from 'lucide-react';

export const CasesDialogs = ({
  ESTADOS, PRIORIDADES, TIPOS_CASO,
  // Create dialog
  showCreateDialog, setShowCreateDialog, newCase, setNewCase,
  availableAlerts, handleCreateCase,
  // Detail dialog
  showDetailDialog, setShowDetailDialog, selectedCase,
  handleUpdateStatus, handleUpdateCaseType, handleUpdatePriority,
  showNoteForm, setShowNoteForm, newNote, setNewNote, handleAddNote,
  newComment, setNewComment, handleAddComment,
  handleSendEmail, navigate, navigateToCase,
  // Approval dialog
  showApprovalDialog, setShowApprovalDialog, approvalData, setApprovalData,
  groups, handleSendToApproval,
  // Derive dialog
  showDeriveDialog, setShowDeriveDialog, deriveData, setDeriveData,
  handleDeriveCase,
  // Bulk assign dialog
  showBulkAssignDialog, setShowBulkAssignDialog,
  selectedCaseIds, bulkAssignData, setBulkAssignData,
  usuarios, handleBulkAssign,
  // KPI config dialog
  showKpiConfigDialog, setShowKpiConfigDialog,
  kpiCardsConfig, setKpiCardsConfig, saveKpiCardsConfig,
}) => (
  <>
    {/* Create Case Dialog */}
    <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
      <DialogContent className="max-w-lg" style={{ backgroundColor: '#ffffff' }}>
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Briefcase className="text-blue-600" />Nuevo Caso de Investigación</DialogTitle></DialogHeader>
        <div className="space-y-4 py-4">
          <div><Label>Título del Caso *</Label>
            <Input value={newCase.titulo} onChange={(e) => setNewCase(c => ({ ...c, titulo: e.target.value }))}
              placeholder="Ej: Investigación Red de Fraude #123" style={{ backgroundColor: '#fff' }} data-testid="case-title-input" /></div>
          <div><Label>Descripción</Label>
            <Textarea value={newCase.descripcion} onChange={(e) => setNewCase(c => ({ ...c, descripcion: e.target.value }))}
              placeholder="Describa el caso..." rows={3} style={{ backgroundColor: '#fff' }} /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Tipo de Caso</Label>
              <Select value={newCase.tipo} onValueChange={(v) => setNewCase(c => ({ ...c, tipo: v }))}>
                <SelectTrigger style={{ backgroundColor: '#fff' }} data-testid="case-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{TIPOS_CASO.map(t => { const Icon = t.icon; return (<SelectItem key={t.value} value={t.value}><span className="flex items-center gap-2"><Icon size={14} />{t.label}</span></SelectItem>); })}</SelectContent>
              </Select></div>
            <div><Label>Prioridad</Label>
              <Select value={newCase.prioridad} onValueChange={(v) => setNewCase(c => ({ ...c, prioridad: v }))}>
                <SelectTrigger style={{ backgroundColor: '#fff' }}><SelectValue /></SelectTrigger>
                <SelectContent>{PRIORIDADES.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
              </Select></div>
          </div>
          <div><Label>Alertas a Vincular (opcional)</Label>
            <p className="text-xs text-slate-500 mb-2">Seleccione alertas sin caso asignado</p>
            <div className="max-h-32 overflow-y-auto border rounded p-2 space-y-1">
              {availableAlerts.length === 0 ? (
                <p className="text-xs text-slate-400">No hay alertas disponibles</p>
              ) : availableAlerts.slice(0, 10).map(alert => (
                <label key={alert.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-slate-50 p-1 rounded">
                  <input type="checkbox" checked={newCase.alertas_ids.includes(alert.id)}
                    onChange={(e) => { if (e.target.checked) setNewCase(c => ({ ...c, alertas_ids: [...c.alertas_ids, alert.id] }));
                      else setNewCase(c => ({ ...c, alertas_ids: c.alertas_ids.filter(id => id !== alert.id) })); }} />
                  <span className="truncate">{alert.titulo}</span>
                </label>
              ))}
            </div></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancelar</Button>
          <Button onClick={handleCreateCase} style={{ backgroundColor: '#2563eb' }} data-testid="submit-case-btn">Crear Caso</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Case Detail Dialog */}
    <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" style={{ backgroundColor: '#ffffff' }}>
        {selectedCase && (
          <>
            <DialogHeader>
              <div className="flex items-center justify-between">
                <DialogTitle className="flex items-center gap-2"><Briefcase className="text-blue-600" />{selectedCase.titulo}</DialogTitle>
                <button onClick={() => { setShowDetailDialog(false); (navigateToCase || ((id) => navigate(`/m/gestion-de-casos/caso/${id}`)))(selectedCase.id); }}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm rounded-md hover:bg-slate-100 text-slate-600" title="Ver a pantalla completa">
                  <Maximize2 size={16} />Pantalla completa
                </button>
              </div>
            </DialogHeader>
            <div className="space-y-6 py-4">
              <div className="flex flex-wrap gap-4 items-start">
                <div><Label className="text-xs text-slate-500">Estado</Label>
                  <Select value={selectedCase.estado} onValueChange={(v) => handleUpdateStatus(selectedCase.id, v)}>
                    <SelectTrigger className="w-44" style={{ backgroundColor: '#fff' }}><SelectValue /></SelectTrigger>
                    <SelectContent>{ESTADOS.map(e => <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div><Label className="text-xs text-slate-500">Tipo de Caso</Label>
                  <Select value={selectedCase.tipo || 'ALERTA'} onValueChange={(v) => handleUpdateCaseType(selectedCase.id, v)}>
                    <SelectTrigger className="w-44" style={{ backgroundColor: '#fff' }}><SelectValue /></SelectTrigger>
                    <SelectContent>{TIPOS_CASO.map(t => { const Icon = t.icon; return <SelectItem key={t.value} value={t.value}><span className="flex items-center gap-2"><Icon size={14} />{t.label}</span></SelectItem>; })}</SelectContent>
                  </Select></div>
                <div><Label className="text-xs text-slate-500">Prioridad</Label>
                  <Select value={selectedCase.prioridad} onValueChange={(v) => handleUpdatePriority(selectedCase.id, v)}>
                    <SelectTrigger className="w-44" style={{ backgroundColor: '#fff' }}><SelectValue /></SelectTrigger>
                    <SelectContent>{PRIORIDADES.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
                  </Select></div>
              </div>
              {selectedCase.descripcion && (
                <div className="bg-slate-50 p-3 rounded"><Label className="text-xs text-slate-500">Descripción</Label>
                  <p className="text-sm text-slate-700 mt-1">{selectedCase.descripcion}</p></div>
              )}
              <div className="grid grid-cols-4 gap-3">
                <Card className="p-3 bg-slate-50"><div className="text-xl font-bold text-slate-900">{selectedCase.total_alertas}</div><div className="text-xs text-slate-500">Alertas</div></Card>
                <Card className="p-3 bg-slate-50"><div className="text-xl font-bold text-slate-900">{selectedCase.total_cuentas}</div><div className="text-xs text-slate-500">Cuentas</div></Card>
                <Card className="p-3 bg-slate-50"><div className="text-xl font-bold text-slate-900">{selectedCase.documentos?.length || 0}</div><div className="text-xs text-slate-500">Documentos</div></Card>
                <Card className="p-3 bg-slate-50"><div className="text-xl font-bold text-slate-900">{selectedCase.timeline?.length || 0}</div><div className="text-xs text-slate-500">Eventos</div></Card>
              </div>
              <Tabs defaultValue="timeline" className="w-full">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="timeline" className="flex items-center gap-1"><History size={14} />Timeline</TabsTrigger>
                  <TabsTrigger value="documentos" className="flex items-center gap-1"><Paperclip size={14} />Documentos</TabsTrigger>
                  <TabsTrigger value="comentarios" className="flex items-center gap-1"><MessageSquare size={14} />Comentarios</TabsTrigger>
                </TabsList>
                <TabsContent value="timeline" className="mt-4">
                  <div className="flex justify-between items-center mb-3">
                    <Label className="text-sm font-medium">Historial de Eventos</Label>
                    <Button variant="outline" size="sm" onClick={() => setShowNoteForm(!showNoteForm)} data-testid="add-note-btn">
                      <Plus size={14} className="mr-1" />Agregar Nota
                    </Button>
                  </div>
                  {showNoteForm && (
                    <Card className="p-3 mb-4 bg-blue-50 border-blue-200">
                      <div className="space-y-3">
                        <div><Label className="text-xs">Tipo de Nota</Label>
                          <Select value={newNote.tipo} onValueChange={(v) => setNewNote(n => ({ ...n, tipo: v }))}>
                            <SelectTrigger className="h-8" style={{ backgroundColor: '#fff' }}><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="LLAMADA">Llamada</SelectItem><SelectItem value="EMAIL">Email</SelectItem>
                              <SelectItem value="REUNION">Reunión</SelectItem><SelectItem value="INVESTIGACION">Investigación</SelectItem>
                              <SelectItem value="OTRO">Otro</SelectItem>
                            </SelectContent>
                          </Select></div>
                        <div><Textarea value={newNote.texto} onChange={(e) => setNewNote(n => ({ ...n, texto: e.target.value }))}
                          placeholder="Detalle de la nota..." rows={2} style={{ backgroundColor: '#fff' }} /></div>
                        <Button size="sm" onClick={handleAddNote} disabled={!newNote.texto.trim()} data-testid="submit-note-btn">
                          <Plus size={14} className="mr-1" />Guardar Nota
                        </Button>
                      </div>
                    </Card>
                  )}
                  <div className="space-y-3 max-h-64 overflow-y-auto">
                    {selectedCase.timeline?.length === 0 ? (
                      <p className="text-sm text-slate-400 text-center py-4">Sin eventos registrados</p>
                    ) : selectedCase.timeline?.map((ev, idx) => {
                      const noteConfig = {
                        CREACION: { color: 'bg-green-500', icon: Plus },
                        COMENTARIO: { color: 'bg-blue-500', icon: MessageSquare },
                        NOTA: { color: 'bg-pink-500', icon: StickyNote },
                        CAMBIO_ESTADO: { color: 'bg-purple-500', icon: History },
                        ALERTA_AGREGADA: { color: 'bg-amber-500', icon: Tag },
                        DOCUMENTO: { color: 'bg-orange-500', icon: FileText },
                        ASIGNACION: { color: 'bg-cyan-500', icon: Users },
                      }[ev.tipo] || { color: 'bg-slate-400', icon: History };
                      const NIcon = noteConfig.icon;
                      return (
                        <div key={idx} className="relative flex gap-3 pl-8">
                          <div className={`absolute left-0 w-6 h-6 rounded-full ${noteConfig.color} flex items-center justify-center`}>
                            <NIcon size={12} className="text-white" />
                          </div>
                          <div className="flex-1 pb-3 border-b border-slate-100">
                            <p className="text-sm text-slate-800">{ev.descripcion}</p>
                            <div className="flex items-center gap-2 text-xs text-slate-400 mt-1">
                              <span>{ev.usuario}</span>
                              <Calendar size={10} /><span>{new Date(ev.fecha).toLocaleString()}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </TabsContent>
                <TabsContent value="documentos" className="mt-4">
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {selectedCase.documentos?.length === 0 ? (
                      <p className="text-sm text-slate-400 text-center py-4">Sin documentos</p>
                    ) : selectedCase.documentos?.map((doc, idx) => {
                      const icon = doc.tipo?.includes('image') ? Image : doc.tipo?.includes('pdf') ? FileText : FileIcon;
                      const DocIcon = icon;
                      return (
                        <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                          <div className="flex items-center gap-3">
                            <DocIcon size={18} className="text-slate-400" />
                            <div><p className="text-sm font-medium text-slate-800">{doc.nombre}</p>
                              <p className="text-xs text-slate-500">{doc.subido_por} - {new Date(doc.fecha).toLocaleDateString()}</p></div>
                          </div>
                          {doc.url && (
                            <a href={doc.url} target="_blank" rel="noopener noreferrer" className="p-2 rounded hover:bg-slate-200">
                              <ExternalLink size={16} className="text-slate-600" />
                            </a>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </TabsContent>
                <TabsContent value="comentarios" className="mt-4">
                  <div className="space-y-3 max-h-64 overflow-y-auto mb-4">
                    {selectedCase.comentarios?.length === 0 ? (
                      <p className="text-sm text-slate-400 text-center py-4">Sin comentarios</p>
                    ) : selectedCase.comentarios?.map((com, idx) => (
                      <div key={idx} className="p-3 bg-slate-50 rounded-lg">
                        <p className="text-sm text-slate-800">{com.texto}</p>
                        <p className="text-xs text-slate-500 mt-1">{com.usuario} - {new Date(com.fecha).toLocaleString()}</p>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <Input placeholder="Agregar comentario..." value={newComment} onChange={(e) => setNewComment(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleAddComment()} style={{ backgroundColor: '#fff' }} />
                    <Button onClick={handleAddComment} disabled={!newComment?.trim()} style={{ backgroundColor: '#7c3aed' }} className="text-white">Enviar</Button>
                  </div>
                </TabsContent>
              </Tabs>
              {/* Action row */}
              <div className="flex items-center gap-2 pt-2 border-t">
                <Badge className={ESTADOS.find(e => e.value === selectedCase.estado)?.color}>{ESTADOS.find(e => e.value === selectedCase.estado)?.label || selectedCase.estado}</Badge>
                <Badge className={PRIORIDADES.find(p => p.value === selectedCase.prioridad)?.color}>{selectedCase.prioridad}</Badge>
                {selectedCase.asignado_a_nombre && <Badge variant="outline">{selectedCase.asignado_a_nombre}</Badge>}
                <div className="ml-auto flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => handleSendEmail(selectedCase)}><Mail size={14} className="mr-1" />Email</Button>
                  <Button size="sm" onClick={() => { setShowDetailDialog(false); (navigateToCase || ((id) => navigate(`/m/gestion-de-casos/caso/${id}`)))(selectedCase.id); }}
                    style={{ backgroundColor: '#2563eb' }} className="text-white"><Maximize2 size={14} className="mr-1" />Ver Completo</Button>
                </div>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>

    {/* Approval Dialog */}
    <Dialog open={showApprovalDialog} onOpenChange={setShowApprovalDialog}>
      <DialogContent style={{ backgroundColor: '#ffffff' }}>
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Send className="text-blue-600" />Enviar a Aprobación</DialogTitle></DialogHeader>
        <div className="space-y-4 py-4">
          <div><Label>Acción a Solicitar *</Label>
            <Select value={approvalData.accion} onValueChange={(v) => setApprovalData(d => ({ ...d, accion: v }))}>
              <SelectTrigger style={{ backgroundColor: '#fff' }} data-testid="approval-action-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="CERRAR">Cerrar Caso</SelectItem><SelectItem value="ESCALAR">Escalar Caso</SelectItem><SelectItem value="APROBAR">Aprobar Resolución</SelectItem></SelectContent>
            </Select></div>
          <div><Label>Grupo Aprobador *</Label>
            <Select value={approvalData.grupo_aprobador_id} onValueChange={(v) => setApprovalData(d => ({ ...d, grupo_aprobador_id: v }))}>
              <SelectTrigger style={{ backgroundColor: '#fff' }} data-testid="approval-group-select"><SelectValue placeholder="Seleccionar grupo" /></SelectTrigger>
              <SelectContent>{groups.map(g => <SelectItem key={g.id} value={g.id}>{g.nombre}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label>Prioridad</Label>
            <Select value={approvalData.prioridad} onValueChange={(v) => setApprovalData(d => ({ ...d, prioridad: v }))}>
              <SelectTrigger style={{ backgroundColor: '#fff' }}><SelectValue /></SelectTrigger>
              <SelectContent>{PRIORIDADES.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label>Descripción / Justificación *</Label>
            <Textarea value={approvalData.descripcion} onChange={(e) => setApprovalData(d => ({ ...d, descripcion: e.target.value }))}
              placeholder="Explique por qué solicita esta aprobación..." rows={3} style={{ backgroundColor: '#fff' }} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setShowApprovalDialog(false)}>Cancelar</Button>
          <Button onClick={handleSendToApproval} style={{ backgroundColor: '#2563eb' }} data-testid="confirm-approval-btn"><Send size={16} className="mr-2" />Enviar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Derive Dialog */}
    <Dialog open={showDeriveDialog} onOpenChange={setShowDeriveDialog}>
      <DialogContent style={{ backgroundColor: '#ffffff' }}>
        <DialogHeader><DialogTitle className="flex items-center gap-2"><ArrowUpRight className="text-orange-600" />Derivar Caso a Grupo</DialogTitle></DialogHeader>
        <div className="space-y-4 py-4">
          <div><Label>Grupo Destino *</Label>
            <Select value={deriveData.grupo_destino_id} onValueChange={(v) => setDeriveData(d => ({ ...d, grupo_destino_id: v }))}>
              <SelectTrigger style={{ backgroundColor: '#fff' }} data-testid="derive-group-select"><SelectValue placeholder="Seleccionar grupo" /></SelectTrigger>
              <SelectContent>{groups.map(g => <SelectItem key={g.id} value={g.id}>{g.nombre}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label>Nueva Prioridad (opcional)</Label>
            <Select value={deriveData.prioridad || "same"} onValueChange={(v) => setDeriveData(d => ({ ...d, prioridad: v === "same" ? "" : v }))}>
              <SelectTrigger style={{ backgroundColor: '#fff' }}><SelectValue placeholder="Mantener actual" /></SelectTrigger>
              <SelectContent><SelectItem value="same">Mantener actual</SelectItem>{PRIORIDADES.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label>Motivo de Derivación *</Label>
            <Textarea value={deriveData.motivo} onChange={(e) => setDeriveData(d => ({ ...d, motivo: e.target.value }))}
              placeholder="Explique por qué deriva este caso..." rows={3} style={{ backgroundColor: '#fff' }} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setShowDeriveDialog(false)}>Cancelar</Button>
          <Button onClick={handleDeriveCase} style={{ backgroundColor: '#f97316' }} data-testid="confirm-derive-btn"><ArrowUpRight size={16} className="mr-2" />Derivar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Bulk Assign Dialog */}
    <Dialog open={showBulkAssignDialog} onOpenChange={setShowBulkAssignDialog}>
      <DialogContent className="bg-white">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Users size={20} className="text-purple-600" />Asignar Casos ({selectedCaseIds.length} seleccionados)</DialogTitle></DialogHeader>
        <div className="space-y-4 py-4">
          <div><Label>Responsable</Label>
            <Select value={bulkAssignData.responsable_id} onValueChange={(v) => setBulkAssignData({ ...bulkAssignData, responsable_id: v })}>
              <SelectTrigger className="mt-1"><SelectValue placeholder="Seleccione responsable..." /></SelectTrigger>
              <SelectContent><SelectItem value="none">Sin asignar</SelectItem>{usuarios.map(u => <SelectItem key={u.id} value={u.id}>{u.nombre} ({u.email})</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label>Grupo</Label>
            <Select value={bulkAssignData.grupo_id} onValueChange={(v) => setBulkAssignData({ ...bulkAssignData, grupo_id: v })}>
              <SelectTrigger className="mt-1"><SelectValue placeholder="Seleccione grupo..." /></SelectTrigger>
              <SelectContent><SelectItem value="none">Sin asignar</SelectItem>{groups.map(g => <SelectItem key={g.id} value={g.id}>{g.nombre}</SelectItem>)}</SelectContent>
            </Select></div>
          <div className="bg-slate-50 p-3 rounded-lg text-sm text-slate-600">Se asignarán <strong>{selectedCaseIds.length}</strong> caso(s) al responsable y/o grupo seleccionado.</div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setShowBulkAssignDialog(false)}>Cancelar</Button>
          <Button onClick={handleBulkAssign} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="confirm-bulk-assign-btn"><Users size={16} className="mr-2" />Asignar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* KPI Config Dialog */}
    <Dialog open={showKpiConfigDialog} onOpenChange={setShowKpiConfigDialog}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Configurar Tarjetas</DialogTitle></DialogHeader>
        <div className="space-y-2 max-h-[400px] overflow-y-auto py-2">
          {kpiCardsConfig.available_cards?.map(card => {
            const isActive = kpiCardsConfig.active_cards?.includes(card.id);
            return (
              <label key={card.id} className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${isActive ? 'bg-blue-50 border border-blue-200' : 'bg-slate-50 border border-transparent hover:bg-slate-100'}`}
                data-testid={`kpi-config-${card.id}`}>
                <input type="checkbox" checked={isActive}
                  onChange={(e) => { const newActive = e.target.checked ? [...(kpiCardsConfig.active_cards || []), card.id] : (kpiCardsConfig.active_cards || []).filter(id => id !== card.id);
                    setKpiCardsConfig(prev => ({ ...prev, active_cards: newActive })); }} className="w-4 h-4 text-blue-600 rounded" />
                <span className="text-sm font-medium text-slate-700">{card.label}</span>
              </label>
            );
          })}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setShowKpiConfigDialog(false)}>Cancelar</Button>
          <Button onClick={() => saveKpiCardsConfig(kpiCardsConfig.active_cards)} data-testid="save-kpi-config-btn">Guardar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </>
);
